/**
 * Typed error raised by the chat transport when the backend responds with
 * a 410 Gone + ProblemDetail type `agent-unavailable`. The AI SDK propagates
 * the thrown error to `useChat.onError`, where we key off this class to show
 * a dedicated toast, drop the optimistic user bubble, and restore the draft.
 */
export class AgentUnavailableError extends Error {
  readonly status = 410 as const;

  constructor(readonly problemDetail?: unknown) {
    // eslint-disable-next-line i18next/no-literal-string -- sentinel message; consumers key off the error name and show their own translated toast
    super('Agent unavailable');
    this.name = 'AgentUnavailableError';
    Object.setPrototypeOf(this, AgentUnavailableError.prototype);
  }

  static is(error: unknown): error is AgentUnavailableError {
    return (
      error instanceof AgentUnavailableError ||
      (error instanceof Error && error.name === 'AgentUnavailableError')
    );
  }
}

const isAgentUnavailableProblemDetail = (body: unknown): boolean =>
  typeof body === 'object' &&
  body !== null &&
  'type' in body &&
  (body as { type?: unknown }).type === 'agent-unavailable';

/**
 * Wraps a fetch implementation and converts a 410 `agent-unavailable`
 * response into an `AgentUnavailableError`. Other failures pass through
 * unchanged so the existing generic error handling still applies.
 */
export const withAgentUnavailableDetection = (base: typeof fetch): typeof fetch => {
  return async (input, init) => {
    const response = await base(input, init);
    if (response.status === 410) {
      let problemDetail: unknown;
      try {
        // clone() so the original body stays readable if we end up returning the response
        problemDetail = await response.clone().json();
      } catch {
        problemDetail = undefined;
      }
      if (isAgentUnavailableProblemDetail(problemDetail)) {
        throw new AgentUnavailableError(problemDetail);
      }
    }
    return response;
  };
};
